import { useState, type FormEvent } from 'react';
import { AlertCircle, CheckCircle2, KeyRound, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { useCurrentUser } from '@/lib/auth/hooks';

import { useChangeOwnPassword, useMe } from './api';

/**
 * Self-service profile page.
 *   - Top: identity card (email, tenant, roles) — sourced from /auth/me
 *     server-side to confirm the JWT is still valid.
 *   - Bottom: change-password form (verifies old password).
 */
export function ProfilePage() {
  const me = useMe();
  const stored = useCurrentUser();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">My profile</h1>
        <p className="text-sm text-muted-foreground">
          Update your account details and password.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity</CardTitle>
          <CardDescription>From your active session.</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-2 text-sm md:grid-cols-2">
            <Pair label="Email" value={stored?.email ?? me.data?.email ?? '—'} />
            <Pair label="User ID" value={me.data?.userId ?? stored?.sub ?? '—'} />
            <Pair
              label="Tenant"
              value={
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {me.data?.tenantId ?? stored?.tenant_id ?? '—'}
                </code>
              }
            />
            <Pair
              label="Roles"
              value={
                <div className="flex flex-wrap gap-1">
                  {(me.data?.roles ?? stored?.roles ?? []).map((r) => (
                    <span
                      key={r}
                      className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              }
            />
          </dl>
        </CardContent>
      </Card>

      <ChangePasswordCard />
    </div>
  );
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex-1 font-medium">{value}</dd>
    </div>
  );
}

function ChangePasswordCard() {
  const change = useChangeOwnPassword();
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [mismatch, setMismatch] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setMismatch(null);
    if (newPwd !== confirmPwd) {
      setMismatch('New passwords do not match');
      return;
    }
    change.mutate(
      { oldPassword: oldPwd, newPassword: newPwd },
      {
        onSuccess: () => {
          setOldPwd('');
          setNewPwd('');
          setConfirmPwd('');
        },
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          Change password
        </CardTitle>
        <CardDescription>
          Min 8 characters. You will stay signed in with the same token.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="cp-old">Current password</Label>
            <Input
              id="cp-old"
              type="password"
              autoComplete="current-password"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              minLength={8}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              minLength={8}
              required
            />
          </div>

          <div className="md:col-span-2">
            {mismatch ? (
              <p className="mb-2 flex items-center gap-1.5 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" /> {mismatch}
              </p>
            ) : null}
            {change.isError ? <ApiErrorBanner error={change.error} /> : null}
            {change.isSuccess ? (
              <p className="mb-2 flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> Password updated.
              </p>
            ) : null}
            <Button type="submit" disabled={change.isPending}>
              {change.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Update password
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">
              {error.code}
            </code>
          </p>
        ) : null}
      </div>
    </div>
  );
}
